import React, { useEffect, useMemo, useRef } from 'react';

import { useApolloClient } from 'application/providers/DataProvider';
import {
  useAdCampaigns,
  useAdCampaign,
  useAdCampaignCreate,
  usePromoUpload,
  useAdCampaignEnable,
  useAdCampaignRemove,
  useAdCampaignReportCreate,
  useAdCampaignUpdate,
  useAdPromosUpdate,
  useAdCampaignSetTags,
  useAdCampaignInstantLaunch,
} from '@entities/adCampaign';
import { useAccountCompanyIds } from 'application/hooks';
import { DEFAULT_PAGINATION } from 'application/pages/constants';

import {
  AdCampaignsStatusesPureType,
  AdCampaignShortTypeFragment,
  AdCampaignQueryFilter,
  AdCampaignQueryPagination,
  AdCampaignQuerySort,
  AdCampaignPureType,
  AdCampaignsQueryPaginationType,
  MediaAdCampaignsPaginationQueryDocument,
  MediaAdCampaignsPaginationQueryQuery,
  PromoAdInput,
  AdCampaignPureCreateMutationInput,
} from '@shared/api/graphql/generated';

import { AdCampaignModelContextType } from './types';
import { ALL_AD_CAMPAIGN_STATUSES_COUNT, DEFAULT_FILTERS, DEFAULT_PAGINATION_INFO } from './consts';

const AdCampaignModelContext = React.createContext<AdCampaignModelContextType>({} as AdCampaignModelContextType);

export const useAdCampaignModelContext = () => React.useContext(AdCampaignModelContext);

const AdCampaignModelProvider: React.FC<{ children?: React.ReactNode; pageSize?: number }> = ({
  children,
  pageSize = DEFAULT_PAGINATION.pageSize,
}) => {
  const companyIds = useAccountCompanyIds();

  const client = useApolloClient();

  const [adCampaignsRequest, adCampaignsResult] = useAdCampaigns();
  const [getAdCampaignRequest, adCampaignResult] = useAdCampaign();
  const [createAdCampaignRequest, adCampaignCreateResult] = useAdCampaignCreate();
  const promoUpload = usePromoUpload();
  const [enableAdCampaignRequest] = useAdCampaignEnable();
  const [removeAdCampaignRequest, adCampaignRemoveResult] = useAdCampaignRemove();
  const [adCampaignReportCreateRequest, adCampaignReportCreateResult] = useAdCampaignReportCreate();
  const [updateAdCampaignRequest, adCampaignUpdateResult] = useAdCampaignUpdate();
  const [adCampaignPromoUpdateRequest, adCampaignPromoUpdateResult] = useAdPromosUpdate();
  const [setTagsRequest] = useAdCampaignSetTags();
  const {
    request: adCampaignInstantLaunchRequest,
    result: adCampaignInstantLaunchResult,
    abortRequest: adCampaignInstantLaunchAbort,
  } = useAdCampaignInstantLaunch();

  const adCampaign = adCampaignResult.data.adCampaign;
  const adCampaignLoading = adCampaignResult.loading;
  const adCampaignCreateLoading = adCampaignCreateResult.loading;
  const adCampaignCreateError = adCampaignCreateResult.error;
  const adCampaignRemoveLoading = adCampaignRemoveResult.loading;
  const adCampaignRemoveError = adCampaignRemoveResult.error;
  const adCampaignUpdateLoading = adCampaignUpdateResult.loading;
  const adCampaignUpdateError = adCampaignUpdateResult.error;
  const adCampaignCreateReportLoading = adCampaignReportCreateResult.loading;
  const adCampaignCreateReportErrors = adCampaignReportCreateResult.data.errors;
  const adCampaignPromoUpdateLoading = adCampaignPromoUpdateResult.loading;
  const adCampaignPromoUpdateError = adCampaignPromoUpdateResult.error;
  const adCampaignInstantLaunchLoading = adCampaignInstantLaunchResult.loading;
  const adCampaignInstantLaunchError = adCampaignInstantLaunchResult.error;

  const getAdCampaigns = async ({
    filters,
    pagination,
    sort,
  }: {
    filters: AdCampaignQueryFilter;
    pagination: AdCampaignQueryPagination;
    sort: AdCampaignQuerySort;
  }) => {
    await adCampaignsRequest(filters, pagination, sort);
  };

  const readAdCampaigns = ({
    filters,
    pagination,
    sort,
  }: {
    filters: AdCampaignQueryFilter;
    pagination: AdCampaignQueryPagination;
    sort: AdCampaignQuerySort;
  }) => {
    const result = client.readQuery<MediaAdCampaignsPaginationQueryQuery>({
      query: MediaAdCampaignsPaginationQueryDocument,
      variables: { filters, pagination, sort },
    });

    return (result?.adcampaignsPaginationQuery as AdCampaignsQueryPaginationType) || null;
  };

  const getMoreAdCampaigns = async ({
    filters,
    pagination,
    sort,
  }: {
    filters: AdCampaignQueryFilter;
    pagination: AdCampaignQueryPagination;
    sort: AdCampaignQuerySort;
  }) => {
    await adCampaignsResult.loadMore(filters, pagination, sort);
  };

  const adCampaignsRefetch = async ({
    filters,
    pagination,
    sort,
  }: {
    filters: AdCampaignQueryFilter;
    pagination: AdCampaignQueryPagination;
    sort: AdCampaignQuerySort;
  }) => {
    const result = await adCampaignsResult.refetch({ filters, pagination, sort });
    return (result.data?.adcampaignsPaginationQuery.result || []) as AdCampaignShortTypeFragment[];
  };

  const getAdCampaign = async (id: string) => {
    const result = await getAdCampaignRequest(id);
    return result.data?.adCampaignPure as AdCampaignPureType;
  };

  const filtersRef = useRef<AdCampaignQueryFilter>({
    ...DEFAULT_FILTERS,
    companyIds,
  });
  const paginationRef = useRef<AdCampaignQueryPagination>({
    page: DEFAULT_PAGINATION_INFO.page,
    pageSize: DEFAULT_PAGINATION_INFO.pageSize,
  });
  const sortRef = useRef<AdCampaignQuerySort>(AdCampaignQuerySort.IdDesc);

  const cachedAdCampaigns = readAdCampaigns({
    filters: filtersRef.current,
    pagination: paginationRef.current,
    sort: sortRef.current,
  });

  const resetFilters = () => {
    filtersRef.current = {
      ...DEFAULT_FILTERS,
      mediaType: filtersRef.current.mediaType,
      companyIds,
    };

    paginationRef.current = {
      page: DEFAULT_PAGINATION_INFO.page,
      pageSize: DEFAULT_PAGINATION_INFO.pageSize,
    };

    sortRef.current = AdCampaignQuerySort.IdDesc;
  };

  useEffect(() => () => resetFilters(), []);

  const requestAdCampaigns = async (filters: AdCampaignQueryFilter = DEFAULT_FILTERS) => {
    filtersRef.current = {
      ...filtersRef.current,
      ...filters,
      companyIds,
    };

    paginationRef.current.page = 0;

    await getAdCampaigns({
      filters: filtersRef.current,
      pagination: paginationRef.current,
      sort: sortRef.current,
    });
  };

  const refetchAdCampaigns = async (filters: AdCampaignQueryFilter = DEFAULT_FILTERS) => {
    filtersRef.current = {
      ...filtersRef.current,
      ...filters,
      companyIds,
    };

    paginationRef.current.page = 0;

    await adCampaignsRefetch({
      filters: filtersRef.current,
      pagination: paginationRef.current,
      sort: sortRef.current,
    });
  };

  const readAdCampaignsWithDefaultFilters = (
    filters: AdCampaignQueryFilter = DEFAULT_FILTERS,
    pagination: AdCampaignQueryPagination = {
      page: DEFAULT_PAGINATION.page,
      pageSize: pageSize || DEFAULT_PAGINATION.pageSize,
    }
  ) => {
    filtersRef.current = {
      ...filtersRef.current,
      ...filters,
      companyIds,
    };

    paginationRef.current = {
      ...paginationRef.current,
      ...pagination,
    };

    const result = readAdCampaigns({
      filters: filtersRef.current,
      pagination: paginationRef.current,
      sort: sortRef.current,
    });

    return result;
  };

  const loadMoreAdCampaigns = async () => {
    const page = (adCampaignsResult.data.paginationInfo?.page || 0) + 1;

    if (page <= paginationRef.current.page) return;

    paginationRef.current.page = page;

    await getMoreAdCampaigns({
      filters: filtersRef.current,
      pagination: paginationRef.current,
      sort: sortRef.current,
    });
  };

  const requestAdCampaign = async (id: string) => {
    await getAdCampaign(id);
  };

  const createAdCampaign = async (
    name: string,
    promoIds: string[],
    companyId: string,
    overrideValues: Partial<AdCampaignPureCreateMutationInput> = {}
  ): Promise<AdCampaignPureType> => {
    const result = await createAdCampaignRequest(name, promoIds, companyId, overrideValues);

    if (result) {
      resetFilters();
      await requestAdCampaigns();
    }

    return result;
  };

  const updateAdCampaign = async (campaign: AdCampaignPureType) => {
    const isOk = await updateAdCampaignRequest(campaign);

    if (isOk) {
      resetFilters();
      await requestAdCampaigns();
    }

    return isOk;
  };

  const removeAdCampaign = async (id: string): Promise<boolean> => {
    const isOk = await removeAdCampaignRequest(id);

    if (isOk) {
      resetFilters();
    }

    return isOk;
  };

  const enableAdCampaign = async (id: string, enable: boolean) => {
    const isOk = await enableAdCampaignRequest(id, enable);

    if (isOk) {
      resetFilters();
      await requestAdCampaigns();
    }

    return isOk;
  };

  const adCampaignSetCompanyTags = async (adCampaignId: string, tagNames: string[]) => {
    const isOk = await setTagsRequest({ adCampaignId, tagNames });

    if (isOk) {
      await requestAdCampaigns(filtersRef.current);
      await requestAdCampaign(adCampaignId);
    }

    return isOk;
  };

  const adCampaignUpdatePromo = async (id: string, adPromos: PromoAdInput[]) => {
    const isOk = await adCampaignPromoUpdateRequest(id, adPromos);
    return isOk;
  };

  const adCampaignCreateReport = async (
    adCampaignIds: string[],
    periodStart: string,
    periodEnd: string,
    recipientEmail?: string
  ) => {
    const isOk = await adCampaignReportCreateRequest(adCampaignIds, periodStart, periodEnd, recipientEmail);
    return isOk;
  };

  const adCampaignInstantLaunch = async (adCampaignItem: AdCampaignShortTypeFragment, pointId: string) => {
    const isOk = await adCampaignInstantLaunchRequest({ adcampaignId: adCampaignItem.id, pointId });
    return isOk;
  };

  const preparedAdCampaigns = useMemo(
    () =>
      cachedAdCampaigns?.result?.map((item) => ({
        id: item.id,
        enable: item.enable,
        title: item.title,
        points: item.points,
        promos: item.promos,
        adPromos: item.adPromos,
        timingSettings: item.timingSettings,
        tags: item.tags,
        type: item.type,
        durationPromos: item.durationPromos,
        replicationRole: item.replicationRole,
        mediaType: item.mediaType,
      })) || [],
    [cachedAdCampaigns]
  );

  const preparedStatusesInfo = useMemo(() => {
    let statusesInfo: AdCampaignsStatusesPureType = {};

    const { data } = adCampaignsResult;

    if (data.statusesInfo) {
      statusesInfo = { ...data.statusesInfo };
      delete statusesInfo.__typename;
    }

    return statusesInfo;
  }, [adCampaignsResult.data.statusesInfo]);

  const adCampaignModel: AdCampaignModelContextType = {
    adCampaigns: preparedAdCampaigns,
    statusesInfo: preparedStatusesInfo,
    paginationInfo: adCampaignsResult.data.paginationInfo || DEFAULT_PAGINATION_INFO,
    filters: filtersRef.current,
    adCampaignsLoading: adCampaignsResult.loading,
    adCampaignsCalled: adCampaignsResult.called,
    adCampaignsNetworkStatus: adCampaignsResult.networkStatus,
    allAdCampaignStatusesCount: ALL_AD_CAMPAIGN_STATUSES_COUNT,
    requestAdCampaigns,
    readAdCampaigns: readAdCampaignsWithDefaultFilters,
    refetchAdCampaigns,
    loadMoreAdCampaigns,
    resetFilters,
    adCampaign,
    promoUpload,
    adCampaignLoading,
    adCampaignCreateLoading,
    adCampaignCreateError,
    adCampaignRemoveLoading,
    adCampaignRemoveError,
    adCampaignUpdateLoading,
    adCampaignUpdateError,
    adCampaignCreateReportLoading,
    adCampaignCreateReportErrors,
    adCampaignPromoUpdateLoading,
    adCampaignPromoUpdateError,
    adCampaignInstantLaunchLoading,
    adCampaignInstantLaunchError,
    requestAdCampaign,
    createAdCampaign,
    updateAdCampaign,
    removeAdCampaign,
    enableAdCampaign,
    adCampaignSetCompanyTags,
    adCampaignUpdatePromo,
    adCampaignCreateReport,
    adCampaignInstantLaunch,
    adCampaignInstantLaunchAbort,
  };

  return <AdCampaignModelContext.Provider value={adCampaignModel}>{children}</AdCampaignModelContext.Provider>;
};

export default AdCampaignModelProvider;
